/**
 * Column configuration for the collaboration creators table.
 * Defines which columns are shown for each tab/status filter.
 */

export type CreatorStatusTab =
  | "all"
  | "invited"
  | "sample_requested"
  | "sample_status"
  | "pending_post"
  | "posted_video"
  | "generated_gmv"
  | "posted_no_gmv"
  | "cancelled"
  | "removed";

export type ColumnId =
  | "name"
  | "status"
  | "invitedAt"
  | "sample"
  | "sampleStatus"
  | "started"
  | "avgGmv"
  | "followers"
  | "weeklyPosts"
  | "actions"
  // Phase 2 - Posted Video tab columns
  | "videoCount"
  | "unitSales"
  | "views"
  | "safetyStatus"
  | "gmvFromVideos"
  | "videosWithSafety"
  | "genderDemo"
  | "matchQuality"
  | "removedBy"
  | "lastAction";

export type ColumnDef = {
  id: ColumnId;
  label: string;
  minWidth: string;
  maxWidth?: string;
  sortable?: boolean;
  sortKey?: string;
};

/**
 * Column definitions for each tab.
 * Phase 1: all, invited, sample_status, pending_post, cancelled
 * Phase 2 (TODO): sample_requested, posted_video
 */
export const TAB_COLUMNS: Record<CreatorStatusTab, ColumnDef[]> = {
  all: [
    { id: "name", label: "Name", minWidth: "280px", sortable: true, sortKey: "nickname" },
    { id: "avgGmv", label: "Avg GMV (All Time)", minWidth: "140px", sortable: true, sortKey: "avgGmv" },
    { id: "followers", label: "Followers", minWidth: "110px", sortable: true, sortKey: "followers" },
    { id: "weeklyPosts", label: "Posts (Last 7 days)", minWidth: "140px" },
    { id: "status", label: "Status", minWidth: "120px" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
  invited: [
    { id: "name", label: "Name", minWidth: "240px", sortable: true, sortKey: "nickname" },
    { id: "status", label: "Status", minWidth: "120px" },
    { id: "invitedAt", label: "Invitation sent", minWidth: "130px", sortable: true, sortKey: "invitedAt" },
    { id: "avgGmv", label: "Avg GMV (All Time)", minWidth: "140px", sortable: true, sortKey: "avgGmv" },
    { id: "followers", label: "Followers", minWidth: "110px", sortable: true, sortKey: "followers" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
  sample_requested: [
    { id: "name", label: "Name", minWidth: "280px", sortable: true, sortKey: "nickname" },
    { id: "sample", label: "Sample", minWidth: "100px" },
    { id: "sampleStatus", label: "Sample Status", minWidth: "130px" },
    { id: "avgGmv", label: "Avg GMV (All Time)", minWidth: "140px", sortable: true, sortKey: "avgGmv" },
    { id: "followers", label: "Followers", minWidth: "110px", sortable: true, sortKey: "followers" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
  sample_status: [
    { id: "name", label: "Name", minWidth: "280px", sortable: true, sortKey: "nickname" },
    { id: "sample", label: "Sample", minWidth: "100px" },
    { id: "sampleStatus", label: "Sample Status", minWidth: "130px" },
    { id: "avgGmv", label: "Avg GMV (All Time)", minWidth: "140px", sortable: true, sortKey: "avgGmv" },
    { id: "followers", label: "Followers", minWidth: "110px", sortable: true, sortKey: "followers" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
  pending_post: [
    { id: "name", label: "Name", minWidth: "220px", sortable: true, sortKey: "nickname" },
    { id: "status", label: "Status", minWidth: "110px" },
    { id: "sample", label: "Sample", minWidth: "110px" },
    { id: "avgGmv", label: "Avg GMV (All Time)", minWidth: "140px", sortable: true, sortKey: "avgGmv" },
    { id: "followers", label: "Followers", minWidth: "110px", sortable: true, sortKey: "followers" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
  posted_video: [
    { id: "name", label: "Name", minWidth: "200px", sortable: true, sortKey: "nickname" },
    { id: "videoCount", label: "Videos (All Time)", minWidth: "120px", sortable: true, sortKey: "videoCount" },
    { id: "sample", label: "Sample", minWidth: "100px" },
    {
      id: "unitSales",
      label: "Unit sales (All Time)",
      minWidth: "140px",
      sortable: true,
      sortKey: "totalUnitSales",
    },
    { id: "views", label: "Views (All Time)", minWidth: "130px", sortable: true, sortKey: "totalViews" },
    { id: "safetyStatus", label: "Safety Status", minWidth: "130px" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
  generated_gmv: [
    { id: "name", label: "Name", minWidth: "220px", sortable: true, sortKey: "nickname" },
    { id: "videoCount", label: "Videos (All Time)", minWidth: "120px", sortable: true, sortKey: "videoCount" },
    { id: "sample", label: "Sample", minWidth: "100px" },
    { id: "avgGmv", label: "Avg GMV (All Time)", minWidth: "140px", sortable: true, sortKey: "avgGmv" },
    { id: "followers", label: "Followers", minWidth: "110px", sortable: true, sortKey: "followers" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
  posted_no_gmv: [
    { id: "name", label: "Name", minWidth: "28%", maxWidth: "28%", sortable: true, sortKey: "nickname" },
    {
      id: "videoCount",
      label: "Videos (All Time)",
      minWidth: "14%",
      maxWidth: "14%",
      sortable: true,
      sortKey: "videoCount",
    },
    { id: "sample", label: "Sample", minWidth: "12%", maxWidth: "12%" },
    { id: "avgGmv", label: "Avg GMV (All Time)", minWidth: "14%", maxWidth: "14%", sortable: true, sortKey: "avgGmv" },
    { id: "followers", label: "Followers", minWidth: "12%", maxWidth: "12%", sortable: true, sortKey: "followers" },
    { id: "actions", label: "", minWidth: "10%", maxWidth: "10%" },
  ],
  removed: [
    { id: "name", label: "Name", minWidth: "220px", sortable: true, sortKey: "nickname" },
    { id: "gmvFromVideos", label: "GMV from Videos", minWidth: "130px" },
    { id: "unitSales", label: "Unit Sales", minWidth: "100px", sortable: true, sortKey: "totalUnitSales" },
    { id: "videosWithSafety", label: "Videos", minWidth: "160px", sortable: true, sortKey: "videoCount" },
    { id: "views", label: "Views", minWidth: "90px", sortable: true, sortKey: "totalViews" },
    { id: "genderDemo", label: "Gender Demo", minWidth: "130px" },
    { id: "matchQuality", label: "Match Quality", minWidth: "160px" },
    { id: "removedBy", label: "Removed By", minWidth: "130px" },
    { id: "lastAction", label: "Last Action", minWidth: "160px" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
  cancelled: [
    { id: "name", label: "Name", minWidth: "220px", sortable: true, sortKey: "nickname" },
    { id: "sample", label: "Sample", minWidth: "100px" },
    { id: "status", label: "Status", minWidth: "120px" },
    { id: "avgGmv", label: "Avg GMV (All Time)", minWidth: "140px", sortable: true, sortKey: "avgGmv" },
    { id: "followers", label: "Followers", minWidth: "110px", sortable: true, sortKey: "followers" },
    { id: "actions", label: "", minWidth: "80px" },
  ],
};

/**
 * Get column configuration for a given tab
 */
export const getColumnsForTab = (tab: CreatorStatusTab): ColumnDef[] => {
  return TAB_COLUMNS[tab] || TAB_COLUMNS.all;
};
